import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createBlock as _createBlock, Fragment as _Fragment, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = ["tabindex", "aria-hidden"]
const _hoisted_2 = ["tabindex", "aria-hidden"]
const _hoisted_3 = {
  key: 0,
  class: "h-[15px] w-[15px]",
  viewBox: "0 0 24 24",
  fill: "none",
  "aria-hidden": "true"
}
const _hoisted_4 = {
  key: 1,
  class: "h-[15px] w-[15px]",
  viewBox: "0 0 24 24",
  fill: "currentColor",
  "aria-hidden": "true"
}
const _hoisted_5 = ["aria-label", "tabindex", "aria-hidden"]
const _hoisted_6 = { class: "border-t border-catering-cream-300 bg-catering-cream-100 p-3" }

import { ChatBubbleLeftRightIcon, ChevronDownIcon, ChevronLeftIcon } from '@heroicons/vue/24/solid'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { useStore } from 'vuex'

import BotComposer from '@/components/catering/bottleBot/BotComposer.vue'
import BotHeader from '@/components/catering/bottleBot/BotHeader.vue'
import BotHistory from '@/components/catering/bottleBot/BotHistory.vue'
import BotMessageList from '@/components/catering/bottleBot/BotMessageList.vue'
import { useSetupDrawer } from '@/components/setup/setupDrawerOpen'
import { createFocusTrap } from '@/composables/useFocusTrap'
import { botPlatformFromNamespace, botWidgetCopy } from '@/config/botWidgetCopy'
import { subscribeBotChannel, unsubscribeBotChannel } from '@/helpers/botChannel'
import {
  clampDockTop,
  defaultDockTop,
  DOCK_MARGIN,
  dockTopFromDrag,
  dockTopFromRatio,
  dockTopRatio,
  dragExceededThreshold,
  HIDE_TRAVEL,
  hideArmed,
  KEYBOARD_STEP,
  maxDockTop,
  panelAnchor,
  rubberBandPull,
  TAB_TRAVEL,
  tabTop
} from '@/helpers/botFabPosition'
import { nextWidgetMode } from '@/helpers/botWidgetMode'
import { useBotWidgetOpen } from '@/helpers/botWidgetOpen'
import {
  loadWidgetPrefs,
  loadWidgetState,
  restoreWidgetState,
  saveWidgetPrefs,
  saveWidgetState
} from '@/helpers/botWidgetStorage'

const WIDE_VIEWPORT = '(min-width: 577px)'
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
const TAB_REVEAL_DELAY = 130
const TIP_HOVER_DELAY = 280
const TIP_FIRST_RUN_DELAY = 900
const TIP_FIRST_RUN_DURATION = 3000
const TIP_OFFSET = 14


export default {
  __name: 'BottleBotWidget',
  props: {
  namespace: { type: String, default: 'bottleBot' }
},
  setup(__props) {

const props = __props
const namespace = props.namespace
const platform = botPlatformFromNamespace(namespace)
const copy = botWidgetCopy(platform)

const store = useStore()
const toast = useToast()
const { isOpen: setupDrawerOpen, assistantStepActive } = useSetupDrawer()
const { openRequests, takePrompt } = useBotWidgetOpen()
const panelRef = ref(null)
const dockRef = ref(null)
const gripRef = ref(null)
const bubbleRef = ref(null)
const tabRef = ref(null)
const ghostRef = ref(null)
let trap = null
let botChannelSubscribed = false

const mode = computed(() => store.state[namespace].mode)
const view = computed(() => store.state[namespace].view)
const typing = computed(() => store.state[namespace].typing)
const sending = computed(() => store.state[namespace].sending)
const messages = computed(() => store.state[namespace].messages)
const messagesCursor = computed(() => store.state[namespace].messagesCursor)
const conversationsCursor = computed(() => store.state[namespace].conversationsCursor)
const sortedConversations = computed(() => store.getters[`${namespace}/sortedConversations`])
const activeConversation = computed(() => store.getters[`${namespace}/activeConversation`])

const setMode = m => store.commit(`${namespace}/SET_MODE`, m)
const setView = v => store.commit(`${namespace}/SET_VIEW`, v)
const fetchConversations = p => store.dispatch(`${namespace}/fetchConversations`, p)
const openConversation = id => store.dispatch(`${namespace}/openConversation`, id)
const startNewConversation = () => store.dispatch(`${namespace}/startNewConversation`)
const sendMessage = c => store.dispatch(`${namespace}/sendMessage`, c)
const renameConversation = p => store.dispatch(`${namespace}/renameConversation`, p)
const deleteConversation = id => store.dispatch(`${namespace}/deleteConversation`, id)
const loadMoreMessages = () => store.dispatch(`${namespace}/loadMoreMessages`)
const refreshActiveConversation = () => store.dispatch(`${namespace}/refreshActiveConversation`)

// Read during setup rather than on mount: a hidden assembly that first painted
// where it was left would slide off screen in front of the merchant on every load.
const savedPrefs = loadWidgetPrefs(namespace)
const wideViewportQuery = window.matchMedia(WIDE_VIEWPORT)
const reducedMotionQuery = window.matchMedia(REDUCED_MOTION)
const stowed = ref(savedPrefs.stowed)
const tabShown = ref(savedPrefs.stowed)
const tipSeen = ref(savedPrefs.tipSeen)
const tipVisible = ref(false)
const dragging = ref(false)
const armed = ref(false)
const viewportHeight = ref(window.innerHeight)
const wideViewport = ref(wideViewportQuery.matches)
// The stored fraction stays untouched by a resize: only the rendered pixel value is
// clamped, so growing the window back returns the launcher to where it was put.
const topRatio = ref(savedPrefs.dockTopRatio)

// Below 577px the assembly is a transient thing that only shows while the panel is
// closed, and the panel it opens is full screen, so a position carried over from a
// desktop would move it for no gain.
const dockTop = computed(() =>
  wideViewport.value
    ? dockTopFromRatio({ ratio: topRatio.value, viewportHeight: viewportHeight.value })
    : defaultDockTop({ viewportHeight: viewportHeight.value })
)
// The deepened shadow is decoration rather than state, so it goes with the scale
// under reduced motion; the grip's colour stays, because it is the state.
const lifted = computed(() => dragging.value && !reducedMotionQuery.matches)
const anchored = computed(() => wideViewport.value && mode.value !== 'expanded')
const draggable = computed(() => wideViewport.value && !stowed.value)
const drawerPushed = computed(() => setupDrawerOpen.value && !stowed.value)

const tabTopFor = top => tabTop({ dockTop: top, viewportHeight: viewportHeight.value })
const drag = { active: false, pointerId: null, startX: 0, startY: 0, startTop: 0, top: 0, dx: 0, moved: false }

// Called from the template rather than cached in a computed: Vue rewrites every
// key of a style binding on any re-render, so a cached position would stamp the
// resting one back over the drag on the frame the grip lights up. The live drag
// lives on a plain object, which the render reads without subscribing to it.
const liveTop = () => (drag.moved ? drag.top : dockTop.value)
const livePush = () => (drag.moved ? rubberBandPull({ dx: drag.dx }) : stowed.value ? HIDE_TRAVEL : 0)

// One transform, not a transform plus a translate: --bot-drawer-x carries the
// setup drawer's push so the drag and the push cannot overwrite one another, and
// the breakpoint that decides whether there is room to be pushed stays in CSS.
const dockStyle = () => ({ transform: `translate3d(calc(var(--bot-drawer-x) + ${livePush()}px), ${liveTop()}px, 0)` })
const ghostStyle = () => ({ transform: `translate3d(0, ${tabTopFor(liveTop())}px, 0)` })
const tabStyle = computed(() => ({
  transform: `translate3d(${tabShown.value ? 0 : TAB_TRAVEL}px, ${tabTopFor(dockTop.value)}px, 0)`
}))
const tipStyle = computed(() => ({
  transform: `translate3d(var(--bot-drawer-x), ${dockTop.value + TIP_OFFSET}px, 0)`
}))

const panelStyle = computed(() => {
  if (!anchored.value) return {}
  const { placement, offset, maxHeight } = panelAnchor({
    dockTop: dockTop.value,
    viewportHeight: viewportHeight.value
  })
  return {
    top: placement === 'below' ? `${offset}px` : 'auto',
    bottom: placement === 'below' ? 'auto' : `${offset}px`,
    maxHeight: `${maxHeight}px`
  }
})

let tabTimer = null
let tipTimer = null

const hideTip = () => {
  clearTimeout(tipTimer)
  tipVisible.value = false
}

const markTipSeen = () => {
  if (tipSeen.value) return
  tipSeen.value = true
  saveWidgetPrefs({ tipSeen: true }, namespace)
}

const setStowed = next => {
  stowed.value = next
  saveWidgetPrefs({ stowed: next }, namespace)
  clearTimeout(tabTimer)
  if (!next) return (tabShown.value = false)
  // The tab slides in partway through the assembly's exit rather than after it:
  // waiting for the exit to land reads as two separate, sluggish movements.
  tabTimer = setTimeout(() => (tabShown.value = true), reducedMotionQuery.matches ? 0 : TAB_REVEAL_DELAY)
  if (mode.value !== 'hidden') setMode('hidden')
}

const hide = () => {
  if (stowed.value) return
  hideTip()
  markTipSeen()
  setStowed(true)
}

const commitDockTop = top => {
  const clamped = clampDockTop({ top, viewportHeight: viewportHeight.value })
  topRatio.value = dockTopRatio({ top: clamped, viewportHeight: viewportHeight.value })
  saveWidgetPrefs({ dockTopRatio: topRatio.value }, namespace)
}

// Straight to the element, not through a ref: a re-render per pointermove drops
// frames, and the transition is off for the duration so nothing lags the pointer.
const paintDrag = () => {
  if (dockRef.value) dockRef.value.style.transform = dockStyle().transform
  if (ghostRef.value) ghostRef.value.style.transform = ghostStyle().transform
}

// Tap to hide is the grip's job at every width; only the repositioning half of it
// waits for a viewport wide enough to have somewhere to reposition to.
const onPointerDown = e => {
  if (stowed.value) return
  if (e.pointerType === 'mouse' && e.button !== 0) return
  e.preventDefault()
  hideTip()
  drag.active = true
  drag.pointerId = e.pointerId
  drag.startX = e.clientX
  drag.startY = e.clientY
  drag.startTop = dockTop.value
  drag.top = dockTop.value
  drag.dx = 0
  drag.moved = false
  e.currentTarget.setPointerCapture?.(e.pointerId)
}

const onPointerMove = e => {
  if (!drag.active || e.pointerId !== drag.pointerId) return
  // A mouse released outside the window can swallow the pointerup, and the grip
  // would then follow the cursor with no button held down.
  if (e.pointerType === 'mouse' && e.buttons === 0) return onPointerUp(e)
  if (!drag.moved) {
    const start = { startX: drag.startX, startY: drag.startY, currentX: e.clientX, currentY: e.clientY }
    if (!dragExceededThreshold(start)) return
    // Past the slop the release is no longer a tap, whether or not this viewport
    // has anywhere to drag to.
    drag.moved = true
    if (draggable.value) {
      dragging.value = true
      if (mode.value !== 'hidden') setMode('hidden')
    }
  }
  if (!draggable.value) return
  drag.dx = e.clientX - drag.startX
  drag.top = dockTopFromDrag({
    startTop: drag.startTop,
    startY: drag.startY,
    currentY: e.clientY,
    viewportHeight: viewportHeight.value
  })
  armed.value = hideArmed({ dx: drag.dx })
  paintDrag()
}

const endDrag = e => {
  if (!drag.active || e.pointerId !== drag.pointerId) return null
  if (e.currentTarget?.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  const gesture = { moved: drag.moved, top: drag.top, dx: drag.dx }
  drag.active = false
  drag.pointerId = null
  drag.moved = false
  drag.dx = 0
  dragging.value = false
  armed.value = false
  return gesture
}

const onPointerUp = e => {
  const gesture = endDrag(e)
  if (!gesture) return
  if (!gesture.moved) return hide()
  if (!draggable.value) return
  commitDockTop(gesture.top)
  markTipSeen()
  if (hideArmed({ dx: gesture.dx })) return hide()
  paintDrag()
}

// The browser takes the gesture away - a scroll starting under the finger where
// the drag is off, a system gesture - so nothing was asked for and nothing is
// hidden. A drag already under way keeps the ground it covered.
const onPointerCancel = e => {
  const gesture = endDrag(e)
  if (!gesture) return
  if (gesture.moved && draggable.value) commitDockTop(gesture.top)
  paintDrag()
}

const focusTab = async () => {
  await nextTick()
  tabRef.value?.focus()
}

// Escape belongs to the panel's focus trap, which listens on the document for as
// long as the panel is up; a cancel here would close the conversation as well.
const onGripKeydown = e => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    hide()
    return focusTab()
  }
  if (!draggable.value) return
  const step = { ArrowUp: -KEYBOARD_STEP, ArrowDown: KEYBOARD_STEP }[e.key]
  if (step !== undefined) {
    e.preventDefault()
    markTipSeen()
    return commitDockTop(dockTop.value + step)
  }
  if (e.key === 'Home') {
    e.preventDefault()
    markTipSeen()
    return commitDockTop(DOCK_MARGIN)
  }
  if (e.key === 'End') {
    e.preventDefault()
    markTipSeen()
    return commitDockTop(maxDockTop({ viewportHeight: viewportHeight.value }))
  }
}

const onGripEnter = () => {
  if (dragging.value || tipSeen.value) return
  clearTimeout(tipTimer)
  tipTimer = setTimeout(() => (tipVisible.value = true), TIP_HOVER_DELAY)
}

const onGripLeave = () => {
  if (!dragging.value) hideTip()
}

const onTabClick = async () => {
  markTipSeen()
  setStowed(false)
  await nextTick()
  gripRef.value?.focus()
}

const onBubbleClick = () => setMode(mode.value === 'hidden' ? 'open' : 'hidden')

const onResize = () => {
  viewportHeight.value = window.innerHeight
}
const onWideViewportChange = e => {
  wideViewport.value = e.matches
}

const onOpen = async id => {
  setView('chat')
  await openConversation(id)
}
const onNew = async () => {
  await startNewConversation()
}
const onSend = async content => {
  try {
    await sendMessage(content)
  } catch (error) {
    const status = error?.response?.status
    if (status === 429) {
      const retry = error.response.data?.retry_after
      toast.error(
        retry
          ? `You're sending messages a little too fast. Try again in ${retry}s.`
          : "You're sending messages a little too fast. Please wait a moment."
      )
    } else if (status === 409 && error.response.data?.error === 'bot_unavailable') {
      toast.error('The assistant is currently unavailable. Please try again later.')
    } else {
      toast.error('Sorry — your message could not be sent. Please try again.')
    }
  }
}

// Focus trap + continuity when the panel opens/closes. Anything that puts the panel
// up also brings the launcher back, so no caller has to know about the hidden state.
watch(mode, async (m, previousMode) => {
  if (m !== 'hidden' && stowed.value) setStowed(false)
  saveWidgetState({ mode: m, conversationId: store.state[namespace].activeConversationId }, namespace)
  if (m === 'hidden') {
    trap?.deactivate()
    trap = null
    await nextTick()
    // A drag closes the panel as it starts; taking focus mid-gesture would fight
    // the pointer capture the grip is holding.
    if (!dragging.value) (stowed.value ? tabRef.value : bubbleRef.value)?.focus()
    return
  }
  if (!store.state[namespace].conversationsLoaded) await fetchConversations()
  if (previousMode === 'hidden') refreshActiveConversation()
  await nextTick()
  if (panelRef.value) {
    trap?.deactivate()
    trap = createFocusTrap(panelRef.value, { onEscape: () => setMode('hidden') })
    trap.activate()
  }
})

// A request from elsewhere in the app only has to reach a panel that is not already
// up: an open one is what the caller asked for, and an expanded one is more than it.
// A prompt that came with it is sent as the merchant's own message, so the answer
// arrives in the conversation rather than as something the panel opened onto.
watch(openRequests, async () => {
  if (mode.value === 'hidden') setMode(nextWidgetMode(mode.value, 'launch'))
  const prompt = takePrompt()
  if (!prompt) return
  setView('chat')
  await nextTick()
  await onSend(prompt)
})

const trySubscribeBotChannel = () => {
  if (botChannelSubscribed) return
  const pusher = store.state.pusher
  const userId = store.getters.loggedInUserId
  if (!pusher || !userId) return
  subscribeBotChannel(pusher, userId, data => store.dispatch(`${namespace}/receivePushedMessage`, data))
  botChannelSubscribed = true
}

watch(() => [store.state.pusher, store.getters.loggedInUserId], trySubscribeBotChannel, {
  immediate: true
})

onMounted(() => {
  window.addEventListener('resize', onResize)
  wideViewportQuery.addEventListener('change', onWideViewportChange)
  restoreWidgetState({ openConversation, setMode }, loadWidgetState(namespace), namespace)
  // Touch has no hover to hang the hint off, so it gets one unprompted showing and
  // then never again once the merchant has moved or hidden the launcher themselves.
  if (tipSeen.value) return
  tipTimer = setTimeout(() => {
    tipVisible.value = true
    tipTimer = setTimeout(() => (tipVisible.value = false), TIP_FIRST_RUN_DURATION)
  }, TIP_FIRST_RUN_DELAY)
})

onBeforeUnmount(() => {
  trap?.deactivate()
  clearTimeout(tabTimer)
  clearTimeout(tipTimer)
  window.removeEventListener('resize', onResize)
  wideViewportQuery.removeEventListener('change', onWideViewportChange)
  if (botChannelSubscribed) {
    const pusher = store.state.pusher
    const userId = store.getters.loggedInUserId
    if (pusher && userId) unsubscribeBotChannel(pusher, userId)
  }
})

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["bot-widget-root", { 'bot-pushed': drawerPushed.value }])
  }, [
    (mode.value !== 'expanded')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", {
            class: _normalizeClass(["bot-drag-rail pointer-events-none fixed z-[9999]", { 'is-on': dragging.value }]),
            "aria-hidden": "true"
          }, null, 2),
          _createElementVNode("div", {
            ref_key: "ghostRef",
            ref: ghostRef,
            class: _normalizeClass(["bot-tab-ghost pointer-events-none fixed right-0 top-0 z-[9999] h-[46px] w-[26px] rounded-l-[10px] border-[1.5px] border-dashed", { 'is-on': armed.value }]),
            style: _normalizeStyle(ghostStyle()),
            "aria-hidden": "true"
          }, null, 6),
          (tipVisible.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "bot-tip pointer-events-none fixed right-[110px] top-0 z-[10001] whitespace-nowrap rounded-catering-xs bg-catering-ink-900 px-2.5 py-[7px] text-xs text-catering-cream-100",
                style: _normalizeStyle(tipStyle.value),
                role: "status"
              }, " Drag to move · tap to hide ", 4))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            ref_key: "tabRef",
            ref: tabRef,
            type: "button",
            class: _normalizeClass(["bot-tab fixed right-0 top-0 z-[10000] flex h-[46px] w-[26px] items-center justify-center rounded-l-[10px] bg-catering-ink-900 text-catering-lemon shadow-catering-lg outline-none hover:w-[30px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-catering-lemon max-[576px]:hidden", [
          tabShown.value ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
          {
            'max-[576px]:!flex': mode.value === 'hidden',
            'bot-drawer-open': _unref(setupDrawerOpen)
          }
        ]]),
            style: _normalizeStyle(tabStyle.value),
            tabindex: tabShown.value ? 0 : -1,
            "aria-hidden": tabShown.value ? null : 'true',
            "aria-label": "Show chat",
            onClick: onTabClick
          }, [
            _createVNode(_unref(ChevronLeftIcon), { class: "bot-tab-chevron h-4 w-4" })
          ], 14, _hoisted_1),
          _createElementVNode("div", {
            ref_key: "dockRef",
            ref: dockRef,
            class: _normalizeClass(["bot-dock pointer-events-none fixed right-6 top-0 z-[10000] flex select-none items-center max-[576px]:hidden", {
          'max-[576px]:!flex': mode.value === 'hidden',
          'bot-drawer-open': _unref(setupDrawerOpen),
          'is-hidden': stowed.value,
          'is-dragging': dragging.value
        }]),
            style: _normalizeStyle(dockStyle())
          }, [
            _createElementVNode("div", {
              ref_key: "gripRef",
              ref: gripRef,
              class: _normalizeClass(["bot-grip pointer-events-auto -mr-2 box-content flex h-[42px] w-5 cursor-grab items-center justify-center rounded-l-[10px] bg-catering-ink-900 pr-2 outline-none hover:w-6 hover:text-catering-lemon focus-visible:w-6 focus-visible:text-catering-lemon focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-catering-lemon", dragging.value ? 'text-catering-lemon' : 'text-catering-ink-500']),
              role: "button",
              tabindex: stowed.value ? -1 : 0,
              "aria-hidden": stowed.value ? 'true' : null,
              "aria-label": "Hide chat. Drag to reposition.",
              onPointerdown: onPointerDown,
              onPointermove: onPointerMove,
              onPointerup: onPointerUp,
              onPointercancel: onPointerCancel,
              onPointerenter: onGripEnter,
              onPointerleave: onGripLeave,
              onKeydown: onGripKeydown
            }, [
              (armed.value)
                ? (_openBlock(), _createElementBlock("svg", _hoisted_3, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("path", {
                      d: "M9 6l6 6-6 6",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round"
                    }, null, -1)
                  ]))]))
                : (_openBlock(), _createElementBlock("svg", _hoisted_4, [...(_cache[6] || (_cache[6] = [
                    _createStaticVNode("<circle cx=\"9\" cy=\"6\" r=\"1.4\" data-v-d1239e3a></circle><circle cx=\"15\" cy=\"6\" r=\"1.4\" data-v-d1239e3a></circle><circle cx=\"9\" cy=\"12\" r=\"1.4\" data-v-d1239e3a></circle><circle cx=\"15\" cy=\"12\" r=\"1.4\" data-v-d1239e3a></circle><circle cx=\"9\" cy=\"18\" r=\"1.4\" data-v-d1239e3a></circle><circle cx=\"15\" cy=\"18\" r=\"1.4\" data-v-d1239e3a></circle>", 6)
                  ]))]))
            ], 42, _hoisted_2),
            _createElementVNode("button", {
              ref_key: "bubbleRef",
              ref: bubbleRef,
              type: "button",
              "aria-label": mode.value === 'hidden' ? 'Open Bottle Bot' : 'Minimize Bottle Bot',
              tabindex: stowed.value ? -1 : 0,
              "aria-hidden": stowed.value ? 'true' : null,
              class: _normalizeClass(["bot-launcher pointer-events-auto relative flex h-[58px] w-[58px] flex-shrink-0 items-center justify-center rounded-full bg-catering-ink-900 text-catering-lemon outline-none hover:scale-105 hover:bg-catering-ink-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-catering-lemon", [
            lifted.value ? 'shadow-catering-xl' : 'shadow-catering-lg',
            { 'bot-attention': _unref(setupDrawerOpen) && _unref(assistantStepActive) && !stowed.value }
          ]]),
              onClick: onBubbleClick
            }, [
              (mode.value === 'hidden')
                ? (_openBlock(), _createBlock(_unref(ChatBubbleLeftRightIcon), {
                    key: 0,
                    class: "h-[26px] w-[26px]"
                  }))
                : (_openBlock(), _createBlock(_unref(ChevronDownIcon), {
                    key: 1,
                    class: "h-[26px] w-[26px]"
                  }))
            ], 10, _hoisted_5)
          ], 6)
        ], 64))
      : _createCommentVNode("", true),
    (mode.value !== 'hidden')
      ? (_openBlock(), _createElementBlock("section", {
          key: 1,
          ref_key: "panelRef",
          ref: panelRef,
          role: "dialog",
          "aria-modal": "true",
          "aria-label": "Bottle Bot",
          class: _normalizeClass(["bot-widget-panel fixed z-[10000] flex flex-col overflow-hidden bg-catering-cream-100 font-sans shadow-catering-xl", [
        mode.value === 'expanded'
          ? 'inset-0'
          : 'bottom-24 right-6 h-[620px] max-h-[calc(100vh-8rem)] w-[400px] rounded-catering-xl max-[576px]:inset-0 max-[576px]:h-full max-[576px]:w-full max-[576px]:rounded-none',
        { 'bot-drawer-push': _unref(setupDrawerOpen) && mode.value !== 'expanded' }
      ]]),
          style: _normalizeStyle(panelStyle.value)
        }, [
          _createVNode(BotHeader, {
            mode: mode.value,
            platform: _unref(platform),
            title: activeConversation.value ? activeConversation.value.title || 'Bottle Bot' : 'Bottle Bot',
            onHistory: _cache[0] || (_cache[0] = $event => (setView('history'))),
            onNew: onNew,
            onExpand: _cache[1] || (_cache[1] = $event => (setMode(_unref(nextWidgetMode)(mode.value, 'expand')))),
            onMinimize: _cache[2] || (_cache[2] = $event => (setMode(_unref(nextWidgetMode)(mode.value, 'minimize')))),
            onHide: _cache[3] || (_cache[3] = $event => (setMode(_unref(nextWidgetMode)(mode.value, 'hide'))))
          }, null, 8, ["mode", "platform", "title"]),
          (view.value === 'history')
            ? (_openBlock(), _createBlock(BotHistory, {
                key: 0,
                platform: _unref(platform),
                conversations: sortedConversations.value,
                cursor: conversationsCursor.value,
                onOpen: onOpen,
                onRename: renameConversation,
                onDelete: deleteConversation,
                onLoadMore: _cache[4] || (_cache[4] = $event => (fetchConversations({ loadMore: true })))
              }, null, 8, ["platform", "conversations", "cursor"]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createVNode(BotMessageList, {
                  messages: messages.value,
                  typing: typing.value,
                  platform: _unref(platform),
                  "has-more-cursor": !!messagesCursor.value,
                  onLoadMore: loadMoreMessages,
                  onSuggest: onSend
                }, null, 8, ["messages", "typing", "platform", "has-more-cursor"]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(BotComposer, {
                    disabled: sending.value,
                    placeholder: _unref(copy).placeholder,
                    onSubmit: onSend
                  }, null, 8, ["disabled", "placeholder"])
                ])
              ], 64))
        ], 6))
      : _createCommentVNode("", true)
  ], 2))
}
}

}